import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import './OrderList.css';

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    showSizeChanger: true,
    showTotal: (total) => `共 ${total} 条订单`
  });

  const fetchOrders = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/orders/printer/all/', {
        params: {
          page: params.page || pagination.current,
          page_size: params.pageSize || pagination.pageSize,
          search: searchText
        }
      });
      setOrders(response.data.results);
      setPagination({
        ...pagination,
        total: response.data.count,
        current: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [searchText]);

  const handleTableChange = (newPagination) => {
    fetchOrders({
      page: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要导出的订单');
      return;
    }

    message.loading({ content: '正在导出...', key: 'exportLoading' });

    try {
      const response = await axios.post('/api/orders/printer/export/', {
        order_ids: selectedRowKeys.map(id => parseInt(id))
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `订单导出_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: '导出成功', key: 'exportLoading' });
    } catch (error) {
      console.error('Export error:', error);
      message.error({ content: '导出失败', key: 'exportLoading' });
    }
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 80,
    },
    {
      title: '快递单号',
      dataIndex: 'express_number',
      key: 'express_number',
      width: 120,
    },
    {
      title: '收件人',
      key: 'recipient',
      width: 50,
      render: (_, record) => record.label_address ? record.label_recipient : record.recipient_name
    },
    {
      title: '电话',
      key: 'phone',
      width: 80,
      render: (_, record) => record.label_address ? record.label_phone : record.recipient_phone
    },
    {
      title: '地址',
      key: 'address',
      width: 280,
      render: (_, record) => record.label_address ? record.label_address : record.recipient_address
    },
    {
      title: '寄件人',
      dataIndex: 'sender_name',
      key: 'sender_name',
      width: 80,
    },
    {
      title: '寄件人电话',
      dataIndex: 'sender_phone',
      key: 'sender_phone',
      width: 80,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Space>
          <Input.TextArea
            size="small"
            placeholder="搜索订单号/快递单号/收件人/电话&#10;搜索多个订单时，每行一个"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300, minHeight: 60 }}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <Button
            size="small"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={selectedRowKeys.length === 0}
          >
            导出订单
          </Button>
        </Space>
      </div>
      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={columns}
        dataSource={orders}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 940, y: 'calc(100vh - 200px)' }}
        size="small"
        className="compact-table"
      />
    </div>
  );
};

export default OrderList;
